import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import Cropper from "react-easy-crop";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper/modules";
import {
  Edit,
  X,
  Save,
  AlertCircle,
  User,
  Image,
  MapPin,
  Globe,
  Calendar,
  FileText,
  Crop,
  Check,
  BookOpen,
  Building2,
} from "lucide-react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    border: "1px solid #ced4da",
    borderRadius: "0.5rem",
    boxShadow: "none",
    minHeight: "42px",
    fontSize: "0.9rem",
    backgroundColor: "#fff",
    "&:hover": { border: "1px solid #6c757d" },
  }),
  placeholder: (provided) => ({ ...provided, color: "#6c757d" }),
  singleValue: (provided) => ({ ...provided, color: "#212529" }),
};

// Crop helper functions
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new window.Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    }, "image/jpeg");
  });
};

export default function IFProfileEditPage() {
  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;
  const AWS = process.env.REACT_APP_AWS_HOST;
  const teacher_id = sessionStorage.getItem('id');
  const institute_id = sessionStorage.getItem('studentInstituteId');

  const [isEditing, setIsEditing] = useState(false);
  const [statesOptions, setStatesOptions] = useState([]);
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // Crop modal states
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploadChoice, setUploadChoice] = useState(null); // 'crop' or 'direct'

  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    profileImage: null,
    email: "",
    contactNumber: "",
    gender: "",
    dateOfBirth: "",
    address1: "",
    address2: "",
    stateId: null,
    stateName: "",
    cityId: null,
    cityName: "",
    profileSummary: "",
    programIds: "",
  });

  const [instituteInfo, setInstituteInfo] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [showNavigation, setShowNavigation] = useState(false);

  // === Fetch state & city names helper ===
  const fetchStateAndCityNames = useCallback(async (stateId, cityId) => {
    try {
      // Fetch states
      const stateRes = await fetch(`${LMS}/api/get-state`, {
        headers: { Authorization: token, Accept: "application/json" },
      });
      const stateData = await stateRes.json();
      const states = stateData?.data || [];
      const selectedState = states.find(s => s.id === parseInt(stateId));
      
      if (selectedState) {
        setProfile(prev => ({ ...prev, stateName: selectedState.name }));

        // Now fetch cities for this state
        if (cityId) {
          const cityRes = await fetch(`${LMS}/api/get-city?state_id=${stateId}`, {
            headers: { Authorization: token, Accept: "application/json" },
          });
          const cityData = await cityRes.json();
          const cities = cityData?.data || [];
          const selectedCity = cities.find(c => c.id === parseInt(cityId));
          if (selectedCity) {
            setProfile(prev => ({ ...prev, cityName: selectedCity.name }));
          }
        }
      }
    } catch (err) {
      console.warn("Could not load state/city names for display", err);
    }
  }, [LMS, token]);

  // === Fetch Profile ===
  const fetchTeacherProfile = useCallback(async () => {
    try {
      setPageLoading(true);
      const response = await fetch(`${LMS}/api/teacher-details/${teacher_id}`, {
        headers: { Authorization: token, Accept: "application/json" },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to load profile");

      const { teacher_detail, user, institute, programs: programList } = result.data;

      // First set the profile with IDs
      setProfile({
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        profileImage: user.profile_image || null,
        email: user.email || "",
        contactNumber: user.contact_number || "",
        gender: teacher_detail.gender || "",
        dateOfBirth: teacher_detail.date_of_birth || "",
        address1: teacher_detail.address1 || "",
        address2: teacher_detail.address2 || "",
        stateId: teacher_detail.state || null,
        cityId: teacher_detail.city || null,
        profileSummary: teacher_detail.profile_summary || "",
        programIds: teacher_detail.program_ids || "",
        stateName: "",  // will be filled below
        cityName: "",   // will be filled below
      });

      setInstituteInfo(institute);
      setPrograms(programList || []);

      // Now fetch state & city names if IDs exist
      if (teacher_detail.state) {
        await fetchStateAndCityNames(teacher_detail.state, teacher_detail.city);
      }

      setError("");
    } catch (err) {
      setError(err.message || "Failed to load profile data");
    } finally {
      setPageLoading(false);
    }
  }, [LMS, token, teacher_id, fetchStateAndCityNames]);

  // === Navigation visibility ===
  const checkNavigationNeeded = useCallback(() => {
    const screenWidth = window.innerWidth;
    let slidesPerView = screenWidth < 768 ? 1 : screenWidth < 1150 ? 2 : 3;
    setShowNavigation(programs.length > slidesPerView);
  }, [programs.length]);

  useEffect(() => {
    if (programs.length > 0) {
      checkNavigationNeeded();
      window.addEventListener("resize", checkNavigationNeeded);
      return () => window.removeEventListener("resize", checkNavigationNeeded);
    }
  }, [programs.length, checkNavigationNeeded]);

  // === States & Cities ===
  const fetchState = useCallback(async () => {
    try {
      const response = await fetch(`${LMS}/api/get-state`, {
        headers: { Authorization: token, "Content-Type": "application/json", Accept: "application/json" },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to load states");
      setStatesOptions(result?.data?.map(({ id, name }) => ({ label: name, value: id })) || []);
    } catch {
      setStatesOptions([]);
    }
  }, [LMS, token]);

  const fetchCity = useCallback(async (id) => {
    try {
      const response = await fetch(`${LMS}/api/get-city?state_id=${id}`, {
        headers: { Authorization: token, "Content-Type": "application/json", Accept: "application/json" },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to load cities");
      setCitiesOptions(result?.data?.map(({ id, name }) => ({ label: name, value: id })) || []);
    } catch {
      setCitiesOptions([]);
    }
  }, [LMS, token]);

  useEffect(() => { fetchTeacherProfile(); }, [fetchTeacherProfile]);

  useEffect(() => {
    if (isEditing) {
      fetchState();
      if (profile.stateId) fetchCity(profile.stateId);
    }
  }, [isEditing, fetchState, fetchCity, profile.stateId]);

  // === Image Handling ===
  const handleImageSelect = (e) => {
    if (!isEditing) return;
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageSrc(reader.result);
      setUploadChoice(null);
      setShowCropModal(false);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadChoice = (choice) => {
    setUploadChoice(choice);
    if (choice === "crop") {
      setShowCropModal(true);
    } else {
      setProfile((prev) => ({ ...prev, profileImage: imageSrc }));
      setImageSrc(null);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      setProfile((prev) => ({ ...prev, profileImage: croppedImage }));
      setShowCropModal(false);
      setImageSrc(null);
      setUploadChoice(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (e) {
      console.error("Error cropping image:", e);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageSrc(null);
    setUploadChoice(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const onStateSelect = (option) => {
    if (option?.value) fetchCity(option.value);
    else setCitiesOptions([]);
    setProfile((prev) => ({ ...prev, stateId: option?.value || null, cityId: null }));
  };

  const onCitySelect = (option) => {
    setProfile((prev) => ({ ...prev, cityId: option?.value || null }));
  };

  const toggleEditMode = () => {
    if (isEditing) fetchTeacherProfile(); // revert changes
    setIsEditing((prev) => !prev);
    setError("");
    setImageSrc(null);
    setUploadChoice(null);
  };

  const handleInputChange = (e) => {
    if (!isEditing) return;
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!isEditing) return;
  if (!profile.gender || !profile.dateOfBirth) {
    setError("Please fill all mandatory fields.");
    return;
  }

  try {
    setLoading(true);
    const requestBody = {
      institute_id: parseInt(institute_id),
      program_ids: profile.programIds,
      user_id: parseInt(teacher_id),
      gender: profile.gender,
      date_of_birth: profile.dateOfBirth,
      address1: profile.address1,
      address2: profile.address2,
      state: profile.stateId ? parseInt(profile.stateId) : null,
      city: profile.cityId ? parseInt(profile.cityId) : null,
      profile_summary: profile.profileSummary,
      // Send new base64 image if uploaded, otherwise send existing image URL
      profile_image: profile.profileImage?.startsWith("data:") 
        ? profile.profileImage 
        : profile.profileImage || null,
    };

    const response = await fetch(`${LMS}/api/teacher-details/${teacher_id}`, {
      method: "POST",
      headers: {
        Authorization: token,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.message || "Failed to update profile");

    await fetchTeacherProfile();
    setIsEditing(false);
    setError("");
  } catch (err) {
    setError(err.message || "Failed to update profile.");
  } finally {
    setLoading(false);
  }
};

  const renderViewValue = (value, placeholder = "Not provided") => (
    <p className="mb-0 text-dark" >
      {value || placeholder}
    </p>
  );

  if (pageLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="py-4">
        {error && (
          <div className="alert alert-danger d-flex align-items-center mb-4 shadow rounded-3" role="alert">
            <AlertCircle className="me-2" size={20} />
            <div>{error}</div>
          </div>
        )}

        {/* Crop Modal */}
        {showCropModal && (
          <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <Crop className="me-2" size={20} />
                    Crop Profile Image
                  </h5>
                  <button type="button" className="btn-close" onClick={handleCropCancel}></button>
                </div>
                <div className="modal-body" style={{ height: "400px", position: "relative" }}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
                <div className="modal-footer d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2 flex-grow-1">
                    <label className="small text-muted">Zoom:</label>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="form-range flex-grow-1"
                      style={{ maxWidth: "200px" }}
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-secondary" onClick={handleCropCancel}>
                      <X className="me-1" size={16} /> Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleCropSave}>
                      <Check className="me-1" size={16} /> Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profile Summary Card */}
        <div className="card border-1 rounded-4 mb-4 shadow-sm">
          <div className="card-body gap-3 p-3">
            <div className="row p-2">
              <div className="col-12 d-md-none mb-3">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm rounded-pill float-end"
                  onClick={toggleEditMode}
                  disabled={loading}
                >
                  {isEditing ? (
                    <> <X className="me-1" size={16} /> Cancel </>
                  ) : (
                    <> <Edit className="me-1" size={16} /> Edit Profile </>
                  )}
                </button>
              </div>

              <div className="col-md-10">
                <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 text-center text-md-start">
                  {profile.profileImage ? (
                    <img
                      src={profile.profileImage.startsWith("data:") ? profile.profileImage : `${AWS}${profile.profileImage}`}
                      alt="Profile"
                      className="rounded-circle flex-shrink-0"
                      style={{ width: "120px", height: "120px", objectFit: "cover" }}
                    />
                  ) : (
                    <div
                      className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center shadow flex-shrink-0"
                      style={{ width: "120px", height: "120px", fontSize: "2rem", fontWeight: "bold" }}
                    >
                      {profile.firstName?.charAt(0) || "U"}
                    </div>
                  )}
                  <div className="align-self-center">
                    <h4 className="mb-1 fw-bold">
                      {`${profile.firstName} ${profile.lastName}` || "Faculty Profile"}
                    </h4>
                    <p className="mb-1 text-muted small">{profile.email} • {profile.contactNumber}</p>
                    <p className="mb-0 text-muted small">
                      {profile.address1} {profile.address2 && `, ${profile.address2}`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-2 d-none d-md-block">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm rounded-pill float-end"
                  onClick={toggleEditMode}
                  disabled={loading}
                >
                  {isEditing ? (
                    <> <X className="me-1" size={16} /> Cancel </>
                  ) : (
                    <> <Edit className="me-1" size={16} /> Edit Profile </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Personal Info Card */}
        <div className="card border-1 rounded-4 mb-4 shadow-sm">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">Personal Information</h5>

            <div className="row g-4 align-items-center">
              {/* Gender */}
              <div className="col-md-6 mb-2 mt-4">
                <label className="form-label small text-muted fw-bold">
                  <User className="me-1" size={15} /> Gender *
                </label>
                {isEditing ? (
                  <div className="d-flex gap-3 mt-1">
                    {["male", "female", "other"].map((opt) => (
                      <div className="form-check" key={opt}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="gender"
                          value={opt}
                          checked={profile.gender === opt}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">{opt.charAt(0).toUpperCase() + opt.slice(1)}</label>
                      </div>
                    ))}
                  </div>
                ) : (
                  renderViewValue(profile.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : "")
                )}
              </div>

              {/* Date of Birth */}
              <div className="col-md-6 mb-2 mt-4">
                <label className="form-label small text-muted fw-bold">
                  <Calendar className="me-1" size={15} /> Date of Birth *
                </label>
                {isEditing ? (
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={profile.dateOfBirth}
                    onChange={handleInputChange}
                    className="form-control rounded-3 shadow-sm"
                    required
                  />
                ) : (
                  renderViewValue(
                    profile.dateOfBirth
                      ? new Date(profile.dateOfBirth).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : ""
                  )
                )}
              </div>

              {/* Profile Image Upload (Now matches InstituteProfilePage) */}
              {isEditing && (
                <div className="col-12 mb-4">
                  <label className="form-label small text-muted fw-bold">
                    <Image className="me-1" size={15} /> Profile Image
                  </label>

                  {/* 1. File Input */}
                  {!imageSrc && !profile.profileImage?.startsWith("data:") && (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control rounded-3 shadow-sm"
                        onChange={handleImageSelect}
                      />
                      <small className="text-muted">Select a new profile picture</small>
                    </>
                  )}

                  {/* 2. Preview + Choice */}
                  {imageSrc && !showCropModal && (
                    <div className="mt-3 p-3 border rounded-3 bg-light">
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <img
                          src={imageSrc}
                          alt="Preview"
                          className="rounded-circle"
                          style={{ width: "100px", height: "100px", objectFit: "cover" }}
                        />
                        <div>
                          <p className="mb-1 small fw-medium">Selected Image</p>
                          <p className="mb-0 text-muted" style={{ fontSize: "0.8rem" }}>
                            Choose upload method:
                          </p>
                        </div>
                      </div>

                      <div className="d-flex gap-3">
                        <button
                          type="button"
                          className={`btn btn-outline-primary btn-sm flex-fill ${uploadChoice === "crop" ? "active" : ""}`}
                          onClick={() => handleUploadChoice("crop")}
                        >
                          <Crop size={14} className="me-1" />
                          Crop & Upload
                        </button>
                        <button
                          type="button"
                          className={`btn btn-outline-success btn-sm flex-fill ${uploadChoice === "direct" ? "active" : ""}`}
                          onClick={() => handleUploadChoice("direct")}
                        >
                          <Check size={14} className="me-1" />
                          Upload Directly
                        </button>
                      </div>

                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0"
                          onClick={() => {
                            setImageSrc(null);
                            setUploadChoice(null);
                          }}
                        >
                          Change Image
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 3. Ready to save indicator */}
                  {profile.profileImage?.startsWith("data:") && (
                    <div className="mt-3 p-3 border rounded-3 bg-success-subtle">
                      <div className="d-flex align-items-center gap-2">
                        <Check size={16} className="text-white" />
                        <span className="small text-white fw-medium">New profile image ready to save</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Profile Summary */}
              <div className="col-12">
                <label className="form-label small text-muted fw-bold">
                  <FileText className="me-1" size={15} /> Profile Summary
                </label>
                {isEditing ? (
                  <textarea
                    name="profileSummary"
                    value={profile.profileSummary}
                    onChange={handleInputChange}
                    className="form-control rounded-3 shadow-sm"
                    rows="4"
                    placeholder="Write a brief summary about yourself..."
                  />
                ) : (
                  renderViewValue(profile.profileSummary)
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Address Info Card */}
        <div className="card border-1 rounded-4 shadow-sm">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">Address Information</h5>

            <div className="row g-4">
              <div className="col-md-6">
                <label className="form-label small text-muted fw-bold">
                  <MapPin className="me-1" size={15} /> Address Line 1
                </label>
                {isEditing ? (
                  <input
                    name="address1"
                    value={profile.address1}
                    onChange={handleInputChange}
                    className="form-control rounded-3 shadow-sm"
                  />
                ) : (
                  renderViewValue(profile.address1)
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label small text-muted fw-bold">
                  <MapPin className="me-1" size={15} /> Address Line 2
                </label>
                {isEditing ? (
                  <input
                    name="address2"
                    value={profile.address2}
                    onChange={handleInputChange}
                    className="form-control rounded-3 shadow-sm"
                  />
                ) : (
                  renderViewValue(profile.address2)
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label small text-muted fw-bold">
                  <Globe className="me-1" size={15} /> State
                </label>
                {isEditing ? (
                  <Select
                    options={statesOptions}
                    value={statesOptions.find((o) => o.value === profile.stateId) || null}
                    onChange={onStateSelect}
                    placeholder="Select a state..."
                    isClearable
                    styles={customSelectStyles}
                  />
                ) : (
                  renderViewValue(profile.stateName)
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label small text-muted fw-bold">
                  <Globe className="me-1" size={15} /> City
                </label>
                {isEditing ? (
                  <Select
                    options={citiesOptions}
                    value={citiesOptions.find((o) => o.value === profile.cityId) || null}
                    onChange={onCitySelect}
                    placeholder={profile.stateId ? "Select a city..." : "Select state first"}
                    isDisabled={!profile.stateId}
                    isClearable
                    styles={customSelectStyles}
                  />
                ) : (
                  renderViewValue(profile.cityName)
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
                {isEditing && (
                <div className="col-12 d-flex justify-content-end gap-3 mt-0 mb-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-4"
                    onClick={toggleEditMode}
                    disabled={loading}
                  >
                    <X className="me-1" size={16} /> Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary rounded-pill px-4 shadow-sm"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="me-1" size={16} /> Save Changes
                      </>
                    )}
                  </button>
                </div>
              )}
        </div>
        

        {/* Institute Info & Programs (unchanged) */}
        {instituteInfo && (
          <div className="card border-1 rounded-4 mb-4 shadow-sm">
            <div className="card-body p-4">
              <h5 className="fw-bold mb-3">
                <Building2 size={20} className="me-2" />
                Institute Information
              </h5>
              <div className="d-flex align-items-center gap-3">
                {instituteInfo.institute_logo && (
                  <img
                    src={`${AWS}${instituteInfo.institute_logo}`}
                    alt={instituteInfo.institute_name}
                    className="rounded"
                    style={{ width: "60px", height: "60px", objectFit: "contain" }}
                  />
                )}
                <div>
                  <h6 className="mb-1 fw-semibold">{instituteInfo.institute_name}</h6>
                  <p className="mb-0 text-muted small">
                    <MapPin size={14} className="me-1" />
                    {instituteInfo.address1}{instituteInfo.address2 && `, ${instituteInfo.address2}`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {programs.length > 0 && (
          <div className="card border-1 rounded-4 mb-4 shadow-sm">
            <div className="card-body p-4">
              <h5 className="fw-bold mb-3">
                <BookOpen size={20} className="me-2" />
                Teaching Programs
              </h5>
              <div style={{ position: "relative" }}>
                <Swiper
                  spaceBetween={20}
                  slidesPerView={3}
                  pagination={{ clickable: true }}
                  navigation={showNavigation ? { nextEl: ".swiper-button-next-custom-programs", prevEl: ".swiper-button-prev-custom-programs" } : false}
                  autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
                  modules={[Pagination, Navigation, Autoplay]}
                  breakpoints={{ 0: { slidesPerView: 1 }, 768: { slidesPerView: 2 }, 1150: { slidesPerView: 3 } }}
                >
                  {programs.map((program, idx) => (
                    <SwiperSlide key={program.id || idx}>
                      <div className="card h-100 border shadow-sm">
                        <div className="card-img-top" style={{ height: "200px", overflow: "hidden", position: "relative", borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                          {program.program_image ? (
                            <img src={`${AWS}${program.program_image}`} alt={program.program_title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : null}
                          <div style={{
                            position: program.program_image ? "absolute" : "static",
                            top: 0, left: 0, width: "100%", height: "100%",
                            background: `linear-gradient(135deg, ${['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe'][idx % 5]} 0%, ${['#764ba2', '#667eea', '#f5576c', '#f093fb', '#00f2fe'][idx % 5]} 100%)`,
                            display: program.program_image ? "none" : "flex",
                            alignItems: "center", justifyContent: "center", color: "white", fontSize: "24px", fontWeight: "bold", padding: "20px"
                          }}>
                            <div><BookOpen size={48} style={{ marginBottom: "10px" }} /><div>{program.program_title}</div></div>
                          </div>
                        </div>
                        <div className="card-body">
                          <h6 className="card-title fw-semibold mb-2" style={{ minHeight: "48px" }}>{program.program_title}</h6>
                          <p className="card-text small text-muted">
                            <Calendar size={14} className="me-1" />
                            Duration: {program.duration}
                          </p>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                {showNavigation && (
                  <>
                    <div className="swiper-button-prev-custom-programs" style={{ position: "absolute", top: "40%", left: "-20px", transform: "translateY(-50%)", zIndex: 10, width: "40px", height: "40px", backgroundColor: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", cursor: "pointer", border: "1px solid #e9ecef" }}>
                      <span style={{ fontSize: "20px", color: "#6c757d", fontWeight: "bold" }}>‹</span>
                    </div>
                    <div className="swiper-button-next-custom-programs" style={{ position: "absolute", top: "40%", right: "-20px", transform: "translateY(-50%)", zIndex: 10, width: "40px", height: "40px", backgroundColor: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", cursor: "pointer", border: "1px solid #e9ecef" }}>
                      <span style={{ fontSize: "20px", color: "#6c757d", fontWeight: "bold" }}>›</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}